/**
 * TransactionsTab - Admin transactions management tab
 * Full feature parity with inline implementation
 */
import { motion } from 'framer-motion';
import { ArrowUpDown, RotateCcw } from 'lucide-react';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../context/LanguageContext';

export function TransactionsTab({ 
  transactions,
  onRefundClick,
  getStatusIcon,
  getStatusColor,
  formatDate
}) {
  const { t } = useLanguage();

  return (
    <motion.div 
      key="transactions-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" 
      data-testid="transactions-table"
    >
      <div className="px-6 py-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900">{t('dashboard.admin.allTransactions')}</h3>
      </div>
      {transactions.length === 0 ? (
        <div className="p-8 text-center text-slate-500">
          <ArrowUpDown className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>{t('tables.noTransactionsYet')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.amount')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.date')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {transactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-slate-50" data-testid={`transaction-${tx.id}`}>
                  <td className="px-6 py-4 font-mono text-sm text-slate-600">{tx.id?.slice(0, 8)}...</td>
                  <td className="px-6 py-4 capitalize text-slate-900">{tx.transaction_type?.replace(/_/g, ' ')}</td>
                  <td className="px-6 py-4 font-semibold text-slate-900">${tx.amount?.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(tx.status)}`}>
                      {getStatusIcon(tx.status)}
                      {t(`dashboard.status.${tx.status}`) || tx.status}
                    </span>
                    {tx.refund_status && (
                      <span className="ml-1 px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full">{tx.refund_status}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">{formatDate(tx.created_at)}</td>
                  <td className="px-6 py-4">
                    {['completed', 'succeeded', 'settled'].includes(tx.status) && !tx.refund_status && onRefundClick && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-xs"
                        onClick={() => onRefundClick(tx)}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        {t('dashboard.admin.refunds')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}

export default TransactionsTab;
